import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";import { apiRequest } from "../context/AuthContext";
import { ArrowLeft, Clock, List, ChevronDown, ChevronUp, Loader2, AlertTriangle } from "lucide-react";

// ── Markdown component overrides ──────────────────────────────────────────────

const MD_COMPONENTS = {
  h1: ({ children }) => (
    <h1 className="text-2xl font-bold text-slate-900 mt-8 mb-3"
      style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-xl font-bold text-slate-800 mt-6 mb-2"
      style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}>
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-base font-bold text-slate-800 mt-4 mb-1.5">{children}</h3>
  ),
  p: ({ children }) => (
    <p className="text-slate-700 text-sm leading-relaxed mb-3">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ml-5 space-y-1 mb-3 text-slate-700 text-sm">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="list-decimal list-outside ml-5 space-y-1 mb-3 text-slate-700 text-sm">{children}</ol>
  ),
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => (
    <strong className="font-bold text-slate-900">{children}</strong>
  ),
  em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
  a: ({ href, children }) => {
    const isInternal = href && !href.startsWith("http") && !href.startsWith("mailto");
    if (isInternal) {
      return (
        <Link
          to={href}
          className="text-orange-600 underline underline-offset-2 font-medium hover:text-orange-800 transition-colors"
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        className="text-orange-600 underline underline-offset-2 font-medium hover:text-orange-800 transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-orange-400 pl-4 my-3 text-slate-600 text-sm italic">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-6 border-slate-200" />,
  table: ({ children }) => (
    <div className="overflow-x-auto my-4">
      <table className="min-w-full border border-slate-200 rounded-lg text-sm">{children}</table>
    </div>
  ),
  th: ({ children }) => (
    <th className="bg-slate-50 border border-slate-200 px-3 py-2 text-left text-xs font-bold text-slate-700 uppercase tracking-wide">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="border border-slate-200 px-3 py-2 text-slate-700">{children}</td>
  ),
};

// ── Section ID helper ─────────────────────────────────────────────────────────

function sectionId(idx) {
  return `section-${idx}`;
}

// ── Table of Contents ─────────────────────────────────────────────────────────

function TableOfContents({ sections, activeIdx }) {
  const scrollTo = (idx) => {
    const el = document.getElementById(sectionId(idx));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <nav data-testid="toc-sidebar" className="space-y-0.5">
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 mb-3 px-1">
        Contents
      </p>
      {sections.map((s, idx) => (
        <button
          key={s.id}
          onClick={() => scrollTo(idx)}
          data-testid={`toc-item-${idx}`}
          className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
            activeIdx === idx
              ? "bg-orange-50 text-orange-700 border-l-2 border-orange-500"
              : "text-slate-500 hover:bg-slate-50 hover:text-slate-800"
          }`}
        >
          <span className="text-slate-300 font-bold mr-2">
            {String(idx + 1).padStart(2, "0")}
          </span>
          {s.title}
        </button>
      ))}
    </nav>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

export default function LearnModulePage() {
  const { slug }              = useParams();
  const navigate              = useNavigate();
  const [module, setModule]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [progress, setProgress] = useState(0);
  const [activeIdx, setActiveIdx] = useState(0);
  const [tocOpen, setTocOpen] = useState(false);   // mobile ToC toggle
  const sectionRefs           = useRef([]);

  // ── Load module ─────────────────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await apiRequest("get", `/learn/modules/${slug}`);
        if (active) { setModule(data); }
      } catch (err) {
        if (active && err?.response?.status === 404) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [slug]);

  // ── Reading progress bar (scroll %) ─────────────────────────────────────────
  useEffect(() => {
    const handler = () => {
      const el = document.documentElement;
      const total = el.scrollHeight - el.clientHeight;
      setProgress(total > 0 ? Math.min(100, Math.round((el.scrollTop / total) * 100)) : 0);
    };
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  // ── Active section tracking (IntersectionObserver) ──────────────────────────
  const observerRef = useRef(null);

  const registerSection = useCallback((el, idx) => {
    sectionRefs.current[idx] = el;
  }, []);

  useEffect(() => {
    if (!module?.sections?.length) return;
    observerRef.current?.disconnect();

    observerRef.current = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const idx = parseInt(entry.target.getAttribute("data-section-idx"), 10);
            if (!isNaN(idx)) setActiveIdx(idx);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );

    sectionRefs.current.forEach(el => {
      if (el) observerRef.current.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [module]);

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  // ── 404 ──────────────────────────────────────────────────────────────────────
  if (notFound || !module) {
    return (
      <div className="text-center py-24" data-testid="module-not-found">
        <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-slate-700 mb-2"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
          Module Not Found
        </h2>
        <p className="text-slate-400 text-sm mb-6">
          This module does not exist or is not published yet.
        </p>
        <Link to="/learn"
          data-testid="back-to-learn"
          className="inline-flex items-center gap-2 text-sm font-bold text-orange-600 hover:text-orange-800">
          <ArrowLeft className="w-4 h-4" /> Back to Learning Hub
        </Link>
      </div>
    );
  }

  const sections = module.sections || [];

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* ── Reading progress bar (fixed to top of viewport) ──────────────── */}
      <div
        data-testid="reading-progress-bar"
        className="fixed top-0 left-0 z-50 h-1 bg-orange-500 transition-all duration-100"
        style={{ width: `${progress}%` }}
      />

      {/* ── Back + breadcrumb ─────────────────────────────────────────────── */}
      <div className="flex items-center gap-2 mb-6 text-sm">
        <Link
          to="/learn"
          data-testid="back-to-learn"
          className="flex items-center gap-1.5 text-slate-500 hover:text-orange-600 font-semibold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Learning Hub
        </Link>
        <span className="text-slate-300">/</span>
        <span className="text-slate-700 font-semibold truncate max-w-xs">{module.title}</span>
      </div>

      {/* ── Module header ────────────────────────────────────────────────── */}
      <div className="mb-6 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">
            Module {module.order_index}
          </span>
          <span className="text-slate-300">·</span>
          <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
            <Clock className="w-3 h-3" />
            {module.estimated_read_minutes} min read
          </span>
        </div>
        <h1
          className="text-3xl font-bold text-slate-900"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}
          data-testid="module-title"
        >
          {module.title}
        </h1>
        <p className="text-slate-500 mt-2 text-sm max-w-2xl">{module.description}</p>
      </div>

      {/* ── Mobile ToC toggle ────────────────────────────────────────────── */}
      {sections.length > 0 && (
        <div className="lg:hidden mb-5">
          <button
            onClick={() => setTocOpen(o => !o)}
            data-testid="mobile-toc-toggle"
            className="w-full flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-semibold text-slate-700"
          >
            <span className="flex items-center gap-2">
              <List className="w-4 h-4 text-slate-400" />
              {sections.length} sections in this module
            </span>
            {tocOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </button>
          {tocOpen && (
            <div className="mt-2 bg-white border border-slate-200 rounded-xl p-4">
              <TableOfContents sections={sections} activeIdx={activeIdx} />
            </div>
          )}
        </div>
      )}

      {/* ── Main layout: ToC sidebar + article ───────────────────────────── */}
      <div className="flex gap-8 items-start">

        {/* ToC — desktop only, sticky */}
        {sections.length > 0 && (
          <aside className="hidden lg:block w-56 flex-shrink-0 sticky top-20">
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <TableOfContents sections={sections} activeIdx={activeIdx} />
            </div>
            {/* Progress fraction */}
            <div className="mt-3 bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Progress</span>
                <span className="text-xs font-black text-orange-600">{progress}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-orange-500 transition-all duration-200 rounded-full"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          </aside>
        )}

        {/* Article */}
        <article
          data-testid="module-content"
          className="flex-1 min-w-0 max-w-2xl"
        >
          {sections.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-400 text-sm">
              Content coming soon.
            </div>
          ) : (
            sections.map((section, idx) => (
              <section
                key={section.id}
                id={sectionId(idx)}
                data-section-idx={idx}
                data-testid={`article-section-${idx}`}
                ref={el => registerSection(el, idx)}
                className="mb-10 scroll-mt-24"
              >
                {/* Section heading */}
                <h2
                  className="text-xl font-bold text-slate-900 mb-4 pb-2 border-b border-slate-100"
                  style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.05em" }}
                >
                  <span className="text-orange-400 font-black mr-2 text-base">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  {section.title}
                </h2>

                {/* Markdown content */}
                <div data-testid={`section-content-${idx}`}>
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={MD_COMPONENTS}
                  >
                    {section.content_md}
                  </ReactMarkdown>
                </div>
              </section>
            ))
          )}

          {/* Footer disclaimer */}
          {sections.length > 0 && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <p className="text-xs text-slate-400 italic leading-relaxed">
                This guide is for informational purposes only. Recruiting rules and NCAA eligibility
                requirements can change. Always verify details with your recruiter, coach, or the
                relevant governing body.
              </p>
              <Link
                to="/learn"
                data-testid="back-to-hub-footer"
                className="inline-flex items-center gap-2 mt-4 text-sm font-bold text-orange-600 hover:text-orange-800 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Learning Hub
              </Link>
            </div>
          )}
        </article>
      </div>
    </div>
  );
}
